import {
  addDays,
  endOfDay,
  isSameDay,
  parse,
  startOfDay,
} from 'date-fns';
import { calculateDistance } from '@utils/helpers';

export const EXPLORE_FILTERS_STORAGE_KEY = 'playfynd_explore_filters_v1';

/** Preset chips vs custom radius (slider). API always uses `distanceKm`. */
export const DEFAULT_EXPLORE_FILTERS = {
  access: 'all',
  ageGroup: 'all',
  verification: 'all',
  dateMode: 'none',
  customDateYmd: '',
  availabilityMode: 'any',
  showFullActivities: true,
  distancePreset: 'any',
  distanceKm: 50,
};

const DISTANCE_PRESETS = ['any', '2', '5', '10', 'custom'];

function inferDistancePreset(km) {
  const k = Number(km);
  if (!Number.isFinite(k) || k >= 50) return 'any';
  if (k === 2) return '2';
  if (k === 5) return '5';
  if (k === 10) return '10';
  return 'custom';
}

/** Keeps `distanceKm` consistent with `distancePreset` and migrates old saved state. */
export function normalizeExploreFilters(f) {
  const merged = { ...DEFAULT_EXPLORE_FILTERS, ...f };
  let preset = merged.distancePreset;
  if (!DISTANCE_PRESETS.includes(preset)) {
    preset = inferDistancePreset(merged.distanceKm);
  }
  merged.distancePreset = preset;

  if (preset === 'any') {
    merged.distanceKm = 50;
  } else if (preset === '2') {
    merged.distanceKm = 2;
  } else if (preset === '5') {
    merged.distanceKm = 5;
  } else if (preset === '10') {
    merged.distanceKm = 10;
  } else {
    let v = Math.round(Number(merged.distanceKm));
    if (!Number.isFinite(v)) v = 15;
    v = Math.min(49, Math.max(1, v));
    merged.distanceKm = v;
    merged.distancePreset = 'custom';
  }

  return merged;
}

export function cloneExploreFilters(f) {
  return normalizeExploreFilters({ ...DEFAULT_EXPLORE_FILTERS, ...f });
}

export function loadPersistedExploreFilters() {
  if (typeof sessionStorage === 'undefined') return cloneExploreFilters({});
  try {
    const raw = sessionStorage.getItem(EXPLORE_FILTERS_STORAGE_KEY);
    if (!raw) return cloneExploreFilters({});
    const parsed = JSON.parse(raw);
    return normalizeExploreFilters({ ...DEFAULT_EXPLORE_FILTERS, ...parsed });
  } catch {
    return cloneExploreFilters({});
  }
}

export function persistExploreFilters(f) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(EXPLORE_FILTERS_STORAGE_KEY, JSON.stringify(f));
  } catch {
    /* ignore */
  }
}

/** Next Sat–Sun window in local time (includes current Sat/Sun if still in weekend). */
export function getThisWeekendRange(from = new Date()) {
  const d = startOfDay(from);
  const dow = d.getDay();
  let sat;
  if (dow === 6) sat = d;
  else if (dow === 0) sat = addDays(d, -1);
  else sat = addDays(d, 6 - dow);
  const sun = addDays(sat, 1);
  return { start: startOfDay(sat), end: endOfDay(sun) };
}

export function exploreFiltersToApiParams(filters) {
  const params = {};
  const r = Number(filters.distanceKm);
  params.radius = Number.isFinite(r) && r > 0 ? r : 50;

  if (filters.access === 'everyone') params.allowedGender = 'everyone';
  else if (filters.access === 'male') params.allowedGender = 'male';
  else if (filters.access === 'female') params.allowedGender = 'female';

  if (filters.verification === 'verified') params.verifiedActivitiesOnly = true;
  if (filters.ageGroup && filters.ageGroup !== 'all') params.ageGroup = filters.ageGroup;

  return params;
}

export function countActiveExploreFilters(filters) {
  let n = 0;
  if (filters.access && filters.access !== 'all') n += 1;
  if (filters.ageGroup && filters.ageGroup !== 'all') n += 1;
  if (filters.verification && filters.verification !== 'all') n += 1;
  if (filters.dateMode && filters.dateMode !== 'none') {
    if (filters.dateMode === 'custom' && !filters.customDateYmd) {
      /* no-op */
    } else {
      n += 1;
    }
  }
  if (filters.availabilityMode && filters.availabilityMode !== 'any') n += 1;
  if (filters.showFullActivities === false) n += 1;
  if (Number(filters.distanceKm) > 0 && Number(filters.distanceKm) < 50) n += 1;
  return n;
}

export function matchesExploreFilters(activity, filters, userLatLng) {
  const maxP = activity.maxParticipants || 0;
  const joined = activity.participants?.length || 0;
  const spotsLeft = maxP - joined;
  const isFull = spotsLeft <= 0;

  if (!filters.showFullActivities && isFull) return false;

  if (filters.availabilityMode === 'hasSpots' && spotsLeft < 1) return false;
  if (filters.availabilityMode === 'almostFull' && (spotsLeft < 1 || spotsLeft > 2)) {
    return false;
  }

  if (filters.verification === 'verified' && activity.verifiedOnly !== true) return false;
  if (filters.verification === 'nonVerified' && activity.verifiedOnly === true) return false;

  if (filters.access === 'everyone') {
    if (!['everyone', 'mixed'].includes(activity.allowedGender)) return false;
  } else if (filters.access === 'male' && activity.allowedGender !== 'male') return false;
  else if (filters.access === 'female' && activity.allowedGender !== 'female') return false;

  if (filters.ageGroup && filters.ageGroup !== 'all') {
    const creatorAge = Number(activity?.creator?.age);
    if (!Number.isFinite(creatorAge)) return false;
    if (filters.ageGroup === 'teens' && (creatorAge < 13 || creatorAge > 17)) return false;
    if (filters.ageGroup === 'youngAdults' && (creatorAge < 18 || creatorAge > 24)) return false;
    if (filters.ageGroup === 'adults' && (creatorAge < 25 || creatorAge > 34)) return false;
    if (filters.ageGroup === 'mature' && creatorAge < 35) return false;
  }

  const dt = new Date(activity.dateTime);
  if (Number.isNaN(dt.getTime())) return false;

  if (filters.dateMode === 'today') {
    if (!isSameDay(dt, new Date())) return false;
  } else if (filters.dateMode === 'tomorrow') {
    if (!isSameDay(dt, addDays(new Date(), 1))) return false;
  } else if (filters.dateMode === 'weekend') {
    const { start, end } = getThisWeekendRange();
    if (dt < start || dt > end) return false;
  } else if (filters.dateMode === 'custom') {
    if (!filters.customDateYmd) {
      /* treat as any day until user picks */
    } else {
      const day0 = parse(filters.customDateYmd, 'yyyy-MM-dd', new Date());
      if (!isSameDay(dt, day0)) return false;
    }
  }

  if (
    userLatLng?.lat != null
    && userLatLng?.lng != null
    && Array.isArray(activity.location?.coordinates)
    && activity.location.coordinates.length === 2
  ) {
    const maxKm = Number(filters.distanceKm);
    if (Number.isFinite(maxKm) && maxKm > 0 && maxKm < 500) {
      const km = calculateDistance(
        userLatLng.lat,
        userLatLng.lng,
        activity.location.coordinates[1],
        activity.location.coordinates[0],
      );
      if (km > maxKm) return false;
    }
  }

  return true;
}
